import { useCallback, useState, useEffect, useMemo, useRef } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { getQueryClient } from "~/widgets/lib/query-client";
import { trackConversion } from "~/widgets/lib/tracking";
import { basketModeFor, parseBasketMode } from "@baluu/ui/lib/checkout/basket-surface";
import { CheckoutFlow, type StripePaymentData } from "../CheckoutFlow";
import { StripePaymentPortal } from "../StripePaymentPortal";
import { SuccessScreenPortal } from "../SuccessScreenPortal";
import { WaitingListPortal } from "../WaitingListPortal";
import type { SuccessData } from "../PortalManager";
import { ensureBasketElement, requestBasketDrawer } from "~/widgets/lib/ensure-basket-element";
import { addLinesToBasket, checkoutLines, type BasketLine } from "~/widgets/lib/basket-lanes";
import { useBasketItemCount, useBasketItems } from "~/widgets/lib/use-basket";
import { useBasketConflict } from "~/widgets/lib/use-basket-conflict";
import { bookingLine, reservedSeats, type FlowItemData } from "./bookingLine";

export type CheckoutOverlayProps = {
  listingId: string;
  businessId: string;
  apiUrl?: string;
  primaryColor?: string;
  theme?: "light" | "dark";
  widgetBaseUrl?: string;
  /** Pre-select a specific session on mount (skips date selection) */
  initialSessionId?: string;
  /** Pre-select a booking type on mount */
  initialBookingType?: string;
  /** Open straight at this full date's waiting list, as the hosted class
   *  page's row does (a v2 class page's "Join the waiting list"), instead of
   *  at the checkout. Closing it closes the overlay. Joining a waiting list
   *  never needs signing in, so the school's sign-in gate (which stands in
   *  front of every checkout flow) doesn't stand in front of it here. */
  waitingListSessionId?: string;
  /** A waiting-list offer being claimed (`offer-ref`: the offer email's
   *  `offer=`, its hold id), as the hosted book-now page takes it: the dates
   *  don't count the claimer's own held seats as taken, and the basket line
   *  carries it so the basket's validation doesn't either. With
   *  `initial-session-id`, the date offered. */
  offerRef?: string;
  /** Business slug for loading theme overrides (falls back to businessId if not provided) */
  businessSlug?: string;
  /** The surface's basket (`BasketMode`): "on" or "page" offers "Add to
   *  basket" beside "Book now" (the flow's choice step) and several dates at
   *  once; none books one thing straight away, as every embed has since the
   *  basket was switched off for the components. */
  basket?: string;
};

type PortalMode = "checkout" | "stripe" | "success" | "waitingList";

export function CheckoutOverlay({
  listingId,
  businessId,
  apiUrl,
  primaryColor = "#03c2b9",
  theme: _theme = "light",
  widgetBaseUrl,
  initialSessionId,
  initialBookingType,
  waitingListSessionId,
  offerRef,
  businessSlug,
  basket,
}: CheckoutOverlayProps) {
  const queryClient = getQueryClient();

  // Portal state management
  const [portalMode, setPortalMode] = useState<PortalMode>(waitingListSessionId ? "waitingList" : "checkout");
  const [stripePaymentData, setStripePaymentData] = useState<StripePaymentData | null>(null);
  const [successData, setSuccessData] = useState<SuccessData | null>(null);

  // The school's basket settings, from the with-bundles response below
  // (basketCheckoutAllowed on the server: off for a school that holds
  // funds). `isEnabled` undefined until it lands, and if it never does.
  const [basketSettings, setBasketSettings] = useState<{
    isEnabled?: boolean;
    enableMultiDateSelection: boolean;
    maxDateSelections: number;
  }>({ enableMultiDateSelection: false, maxDateSelections: 10 });

  // The basket this checkout offers: the surface's, once the school is known
  // to allow one (basketModeFor fails closed: while its settings load, or if
  // they can't be had, one thing at a time — no "Add to basket", no several
  // dates, no basket put on the page).
  const surfaceMode = parseBasketMode(basket);
  const basketMode = basketModeFor(surfaceMode, basketSettings.isEnabled);
  const basketOn = basketMode !== "off";
  // "Book now" keeps what the basket holds unless the surface has no basket
  // or the school is known not to allow one: clearing it is never a guess.
  const keepsBasket = surfaceMode !== "off" && basketSettings.isEnabled !== false;

  // The school's basket (reactive): its count for the choice step, and the
  // seats it already holds for the date picker.
  const basketItems = useBasketItems(businessId);
  const basketItemCount = useBasketItemCount(businessId);
  const reservedSessionQuantities = useMemo(
    () => (basketOn ? reservedSeats(basketItems, listingId) : undefined),
    [basketOn, basketItems, listingId],
  );

  // A refused "Add to basket" / "Book now", shown on the flow's choice step.
  // Judged against the basket "Book now" keeps — which it does while the
  // school's settings are still loading (or failed) too, so the way into it
  // follows the surface's basket, not the not-yet-known mode.
  const {
    conflict: basketConflict,
    refuse,
    onStepChange: handleStepChange,
  } = useBasketConflict({ businessId, apiUrl, primaryColor, mode: keepsBasket ? surfaceMode : "off" });

  // Cached school data for building basket items
  const [schoolName, setSchoolName] = useState<string>("");
  const [courseCurrency, setCourseCurrency] = useState<string>("GBP");

  // Customer-login-gate data, captured from the same with-bundles response
  // below (no extra fetch). `undefined` until that response lands, so
  // CheckoutFlow can tell "not loaded yet" apart from "explicitly false".
  const [schoolSlug, setSchoolSlug] = useState<string | undefined>(undefined);
  const [requireLoginToBook, setRequireLoginToBook] = useState<boolean | undefined>(undefined);

  // The drawer the basket opens in, on a surface that offers it.
  useEffect(() => {
    ensureBasketElement({ businessId, apiUrl, primaryColor, mode: basketMode });
  }, [businessId, apiUrl, primaryColor, basketMode]);

  // The school's basket settings and the sign-in gate, from the course data.
  useEffect(() => {
    const baseUrl = apiUrl || "https://api.baluu.io";
    fetch(`${baseUrl}/widget/courses/${listingId}/with-bundles`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.school?.basket) {
          setBasketSettings({
            isEnabled: data.school.basket.isEnabled === true,
            enableMultiDateSelection: data.school.basket.enableMultiDateSelection ?? false,
            maxDateSelections: data.school.basket.maxDateSelections ?? 10,
          });
        }
        if (data?.school?.name) setSchoolName(data.school.name);
        if (data?.course?.currency) setCourseCurrency(data.course.currency);
        if (data?.school?.slug) setSchoolSlug(data.school.slug);
        // Only commit once the response actually lands — leave `undefined`
        // ("don't gate yet") on fetch failure rather than flipping to a
        // definitive `false`.
        if (data?.school) setRequireLoginToBook(!!data.school.requireLoginToBook);
      })
      .catch(() => {});
  }, [apiUrl, listingId]);

  // Handle close - dispatch custom event and remove element
  const handleClose = useCallback(() => {
    // Dispatch close event
    const event = new CustomEvent("baluu:checkout-close", {
      bubbles: true,
      composed: true,
    });
    document.dispatchEvent(event);

    // Remove this overlay from the DOM
    // Find the baluu-checkout-overlay element and remove it
    const overlays = document.querySelectorAll("baluu-checkout-overlay");
    overlays.forEach((overlay) => {
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
    });
  }, []);

  // Handle completion from checkout flow (free bookings pass partial data)
  const handleComplete = useCallback(
    (data?: Partial<SuccessData>) => {
      const detail = {
        listingId,
        businessId,
        ...data,
      };

      const event = new CustomEvent("baluu:booking-completed", {
        bubbles: true,
        composed: true,
        detail,
      });
      document.dispatchEvent(event);

      trackConversion("baluu_booking_completed", detail);

      setTimeout(() => {
        handleClose();
      }, 3000);
    },
    [listingId, businessId, handleClose],
  );

  // Handle Stripe payment success - switch to success portal
  const handleStripePaymentSuccess = useCallback((data: SuccessData) => {
    setSuccessData(data);
    setPortalMode("success");

    const detail = {
      listingId,
      businessId,
      ...data,
      grossTotal: stripePaymentData?.grossTotal,
      netTotal: stripePaymentData?.netTotal,
      discountAmount: stripePaymentData?.discountAmount,
      discountType: stripePaymentData?.discountType,
    };

    const event = new CustomEvent("baluu:booking-completed", {
      bubbles: true,
      composed: true,
      detail,
    });
    document.dispatchEvent(event);

    trackConversion("baluu_booking_completed", detail);
  }, [listingId, businessId, stripePaymentData]);

  // Handle Stripe portal close (user cancelled)
  const handleStripeClose = useCallback(() => {
    // Go back to checkout
    setPortalMode("checkout");
    setStripePaymentData(null);
  }, []);

  // Handle success screen close
  const handleSuccessClose = useCallback(() => {
    handleClose();
  }, [handleClose]);

  // The session whose waiting list is being joined. The orchestrator/date-step
  // passes the clicked sold-out session id; fall back to the mount session so
  // the initial "Book Now" sold-out case still targets the right date.
  const [waitlistSessionId, setWaitlistSessionId] = useState<string | undefined>(
    waitingListSessionId ?? initialSessionId,
  );
  // The term whose waiting list is being joined. Term cards pass an eventId (and
  // no sessionId) — forwarding it puts the popup in term mode ("This term / Any
  // term / This date") instead of a plain session join.
  const [waitlistEventId, setWaitlistEventId] = useState<string | undefined>(
    undefined,
  );

  // Handle join waiting list - switch to waiting list portal
  const handleJoinWaitingList = useCallback(
    (sessionId?: string, eventId?: string) => {
      setWaitlistSessionId(sessionId ?? initialSessionId);
      setWaitlistEventId(eventId);
      setPortalMode("waitingList");
    },
    [initialSessionId],
  );

  // `waiting-list-session-id` set or changed after the element mounted
  // (the first value is the initial state above): open that date's list.
  // Adjusted while rendering, as React recommends for state that follows a
  // prop, rather than in an effect.
  const [followedWaitingList, setFollowedWaitingList] = useState(waitingListSessionId);
  if (waitingListSessionId !== followedWaitingList) {
    setFollowedWaitingList(waitingListSessionId);
    if (waitingListSessionId) {
      setWaitlistSessionId(waitingListSessionId);
      setWaitlistEventId(undefined);
      setPortalMode("waitingList");
    }
  }

  // Handle waiting list portal close
  const handleWaitingListClose = useCallback(() => {
    handleClose();
  }, [handleClose]);

  // Note: the success `baluu:waiting-list-joined` event is dispatched once by
  // WaitingListPortal itself (with the richer {listingId, sessionId, target,
  // email} detail). We intentionally do NOT pass an onSuccess that re-dispatches
  // it here, to avoid double-counting on the checkout path.

  // One checkout pass hands over its lines in one go: a multi-date pass
  // adds each date but the last through "Add to basket" and pays with the
  // last through "Book now", synchronously. The lines are gathered and go in
  // together, all or none (a refusal leaves no partial booking in the
  // basket): on "Book now" at once, with the basket checkout opening; after
  // adds alone, at the end of the pass, with the drawer opening — once.
  const gathered = useRef<BasketLine[]>([]);

  const lineOf = useCallback(
    (itemData: FlowItemData) => bookingLine(itemData, { businessId, schoolName, currency: courseCurrency, offerRef }),
    [businessId, schoolName, courseCurrency, offerRef],
  );

  // "Add to basket" (the surface offers the basket): the dates join the
  // basket and the visitor carries on browsing.
  const handleAddToBasket = useCallback(
    (itemData: FlowItemData) => {
      gathered.current.push(lineOf(itemData));
      if (gathered.current.length > 1) return;
      queueMicrotask(() => {
        const lines = gathered.current;
        gathered.current = [];
        // "Book now" took them with it.
        if (lines.length === 0) return;
        const added = addLinesToBasket(lines, businessId);
        if (!added.ok) {
          refuse(lines);
          return;
        }
        handleClose();
        requestBasketDrawer(businessId);
      });
    },
    [lineOf, businessId, handleClose, refuse],
  );

  // "Book now": the basket checkout opens to pay — for everything in the
  // basket with it on (the hosted widget's lane), for these lines alone with
  // it off (earlier lines for the school cleared: the single-item lane). The
  // basket is kept unless the school is known not to allow one, so a visitor's
  // basket is never wiped on a guess.
  const handleProceedToBasketCheckout = useCallback(
    (itemData: FlowItemData) => {
      const lines = [...gathered.current, lineOf(itemData)];
      gathered.current = [];
      const outcome = checkoutLines({
        lines,
        businessId,
        apiUrl,
        primaryColor,
        keepBasket: keepsBasket,
      });
      if (outcome.ok) handleClose();
      else refuse(lines);
    },
    [lineOf, businessId, apiUrl, primaryColor, keepsBasket, handleClose, refuse],
  );

  return (
    <>
      {/* Main checkout flow - rendered inside Shadow DOM for CSS isolation */}
      {portalMode === "checkout" && (
        <QueryClientProvider client={queryClient}>
          <CheckoutFlow
            listingId={listingId}
            businessId={businessId}
            apiUrl={apiUrl}
            primaryColor={primaryColor}
            bookingType="regular"
            onClose={handleClose}
            onComplete={handleComplete}
            widgetBaseUrl={widgetBaseUrl}
            onJoinWaitingList={handleJoinWaitingList}
            initialSessionId={initialSessionId}
            initialBookingType={initialBookingType}
            offerRef={offerRef}
            isMultiDateEnabled={basketOn && basketSettings.enableMultiDateSelection}
            maxDateSelections={basketSettings.maxDateSelections}
            basketItemCount={basketItemCount}
            basketConflict={basketConflict}
            onStepChange={handleStepChange}
            reservedSessionQuantities={reservedSessionQuantities}
            onAddToBasket={basketOn ? handleAddToBasket : undefined}
            onProceedToBasketCheckout={handleProceedToBasketCheckout}
            directCheckout={!basketOn}
            schoolSlug={schoolSlug}
            schoolName={schoolName}
            requireLoginToBook={requireLoginToBook}
          />
        </QueryClientProvider>
      )}

      {/* Stripe Payment Portal - rendered via portal to document.body (outside Shadow DOM) */}
      {stripePaymentData && (
        <StripePaymentPortal
          isOpen={portalMode === "stripe"}
          paymentData={stripePaymentData}
          stripePublicKey={stripePaymentData.stripePublicKey || ""}
          primaryColor={primaryColor}
          onSuccess={handleStripePaymentSuccess}
          onClose={handleStripeClose}
        />
      )}

      {/* Success Screen Portal - rendered via portal to document.body (outside Shadow DOM) */}
      {successData && (
        <SuccessScreenPortal
          isOpen={portalMode === "success"}
          successData={successData}
          primaryColor={primaryColor}
          onClose={handleSuccessClose}
        />
      )}

      {/* Waiting List Portal - rendered via portal to document.body (outside Shadow DOM) */}
      <WaitingListPortal
        isOpen={portalMode === "waitingList"}
        listingId={listingId}
        sessionId={waitlistSessionId}
        eventId={waitlistEventId}
        apiUrl={apiUrl}
        primaryColor={primaryColor}
        businessSlug={businessSlug || businessId}
        onClose={handleWaitingListClose}
      />
    </>
  );
}
